import Phaser from 'phaser';
import type { BattleEvent, BattleSetup, TurnRequest } from '../battle/types';
import type { InteractiveBattle } from '../battle/runInteractiveBattle';
import { SPECIES_TO_DEX } from '../battle/teamFromParty';
import { runState } from '../state/runState';
import { audio } from '../systems/audio';
import { TRAINER_BATTLE_MUSIC } from '../data/audioMap';
import {
  mountBattleStatusPanel,
  type BattleStatusPanelHandle,
} from '../ui/battleStatusPanel';
import {
  mountBattleLog,
  BATTLE_LOG_TOP_ESTIMATE_PX,
  type BattleLogHandle,
} from '../ui/battleLog';
import { mountBattleActions, type BattleActionsHandle } from '../ui/battleActions';
import { mountBattleFloatText, type BattleFloatTextHandle } from '../ui/battleFloatText';
import { preloadBattleFx } from '../battle/hitFx';
import {
  mountDialogueOverlay,
  type DialogueOverlayHandle,
} from '../ui/dialogueOverlay';
import { preloadTypeChart } from '../battle/typeChart';
import type { BattleBagRow, BattleUseTarget } from '../battle/battleBag';
import type { SnapshotHandle } from '../render/snapshotBackdrop';
import { buildBattleStage, type BattleStageHandle } from '../render/battleStage';
import { playEvent as playEventImpl, applyStatusTint } from './BattleScene.eventHandlers';
import {
  tweenAttacker as tweenAttackerImpl,
  tweenFaint as tweenFaintImpl,
  runPlayerFaintCinematic as runPlayerFaintCinematicImpl,
  runOpponentDeathFade as runOpponentDeathFadeImpl,
  runVictoryHero as runVictoryHeroImpl,
  runDefeatCinematic as runDefeatCinematicImpl,
  exitToWorld as exitToWorldImpl,
  exitToEndRun as exitToEndRunImpl,
} from './BattleScene.cinematics';
import {
  waitForChoicesReady as waitForChoicesReadyImpl,
  signalChoicesReady as signalChoicesReadyImpl,
  onBackClick as onBackClickImpl,
  hideActionButtons as hideActionButtonsImpl,
  onActionButtonClick as onActionButtonClickImpl,
  showMainMenu as showMainMenuImpl,
  showMoveMenu as showMoveMenuImpl,
  showBagMenu as showBagMenuImpl,
  showBagTargetMenu as showBagTargetMenuImpl,
  confirmBagUse as confirmBagUseImpl,
  showSwitchMenu as showSwitchMenuImpl,
  promptAction as promptActionImpl,
  promptForceSwitch as promptForceSwitchImpl,
  runFromBattle as runFromBattleImpl,
  flushSurvivorHpToRunState as flushSurvivorHpToRunStateImpl,
  flushBattleStatusToRunState as flushBattleStatusToRunStateImpl,
  spliceFaintedFromRunState as spliceFaintedFromRunStateImpl,
  run as runImpl,
  appendLog as appendLogImpl,
  applyHpBarType as applyHpBarTypeImpl,
  updateLastAliveCriticalState as updateLastAliveCriticalStateImpl,
  wait as waitImpl,
  refreshPlayerExp as refreshPlayerExpImpl,
} from './BattleScene.choiceLoop';

export const SPRITE_SCALE = 4; // gen-3 battle sprites are 64×64; render at 4x for screen presence

export type ActionResult =
  | { kind: 'move'; slot: number }
  | { kind: 'switch'; slot: number }
  /** G10: an item turn. useItemTurn already submitted the phantom
   *  choice to the sim inside confirmBagUse — the choice loop submits
   *  NOTHING for this action and loops back to await the next request. */
  | { kind: 'item' }
  | { kind: 'run' };

/** G10: a bag-target row — battleBag's pure target view plus the
 *  display/orchestration fields BattleScene needs to act on a pick. */
export interface BagTargetView extends BattleUseTarget {
  id: string;
  /** 1-indexed sim party slot (runState party order = sim team order;
   *  order can't change mid-battle — reorder UI is world-only). */
  simSlot: number;
  level: number;
  active: boolean;
}

/** Initial-data payload — passed via this.scene.start('BattleScene', data). */
export interface BattleSceneData {
  setup: BattleSetup;
  /** Dex IDs to load front (opponent) and back (player) sprites. */
  opponentDexId: number;
  playerDexId: number;
  /** Phase 12 M4: scriptRef of the trainer NPC for trainer-mode battles.
   *  Present only for trainer LOS encounters (not wild battles). On p1
   *  victory, BattleScene marks this scriptRef as defeated in runState
   *  so the trainer doesn't re-trigger on subsequent passes. */
  trainerScriptRef?: string;
  /** Pokédex Step 7: the map id where the battle was triggered. Stamped
   *  onto careerStore entries via markDied on player faints so the
   *  CAREER tab can render "☠ Onix (Pewter Gym)". */
  battleZone: string;
  /** Glass-Over-World PR 2: WorldScene's frozen snapshot of the world
   *  at the moment the encounter triggered. The handle is parented to
   *  WorldScene at this point; create()'s FIRST line calls
   *  transferOwnership(this) to move the Image onto BattleScene while
   *  the DynamicTexture survives in the global TextureManager. Null
   *  when captureFrom timed out / errored — create() then falls back
   *  to the prior `#0E1014` opaque backdrop. */
  snapshotHandle?: SnapshotHandle | null;
}

/**
 * Auto-playing battle scene. The full BattleEvent[] is fetched up-front by
 * runBattle (deterministic, completes in <10ms in @pkmn/sim) and then
 * animated one event at a time with delays so the visuals can play out.
 *
 * Phase 8 follow-up will add interactive move picking — this scene is the
 * scaffolding; the playEvent state machine is where 'request choice' events
 * will hook in.
 */
export class BattleScene extends Phaser.Scene {
  // Renamed from `data` to avoid colliding with Phaser.Scene.data (DataManager).
  params!: BattleSceneData;

  opponentSprite!: Phaser.GameObjects.Image;
  playerSprite!: Phaser.GameObjects.Image;

  /** Future Cartridge DOM-overlay status panel (PR 3). One per side
   *  — composes the name + level header, the HP bar (inline-mounted),
   *  the HP numeric (player only), and the EXP bar (player only) into
   *  one glass card per Pokémon. Replaces three m5x7 BitmapText fields
   *  + the standalone HP bar's corner-anchoring. The handle proxies
   *  setHp / setType / triggerSweep / triggerFlash / triggerShake to
   *  the inner HP bar so the existing damage-event call sites work
   *  unchanged. */
  opponentStatusPanel?: BattleStatusPanelHandle;
  playerStatusPanel?: BattleStatusPanelHandle;

  /** Future Cartridge DOM-overlay battle log (PR 3). The legacy Phaser
   *  Rectangle + m5x7 BitmapText surface retired here; the handle owns
   *  the bottom-center glass panel + line trimming + viewport resize. */
  battleLogHandle?: BattleLogHandle;

  /** Future Cartridge DOM-overlay dialogue, reused from WorldScene's
   *  dialogueOverlay module. Currently mounted but never shown — the
   *  "{Name} has been lost." faint moment renders via the battle log
   *  (the overlay-based variant hung force-switches; see
   *  runPlayerFaintCinematic). Future polish (battle-start "Wild X
   *  appeared!", super-effective callouts) can drive it directly. */
  dialogueOverlay?: DialogueOverlayHandle;

  /** Liquid Glass stage behind the battlers — blurred frozen-world
   *  snapshot + scrim wash + glass panel, all in-canvas at depth 0
   *  (DOM can't render below the opaque canvas). Self-destroys on
   *  SHUTDOWN; layout() re-runs its geometry on resize. */
  battleStage?: BattleStageHandle;

  /** Current displayed HP percent so the drain tween starts from the right value. */
  opponentHpPercent = 100;
  playerHpPercent = 100;

  /**
   * Action UI: 2x2 grid of buttons that switches between two modes per turn:
   *
   *   'main'  — FIGHT / POKEMON / BAG / RUN (cartridge-style top-level menu)
   *   'moves' — populated with the 4 move slots once the player picks FIGHT
   *
   * 'none' between turns / during animations. Click handlers dispatch off
   * `menuMode` so the same 4 GameObjects are reused for both layers.
   */
  /** Future Cartridge DOM-overlay action buttons (PR 3). The legacy
   *  4-Phaser-Rectangle grid retired here; the handle owns the bottom-
   *  right anchored glass buttons + BACK + iOS click defensive grace
   *  timer + type-color focus borders. Subscription to onTap dispatches
   *  to onActionButtonClick / onBackClick. */
  battleActionsHandle?: BattleActionsHandle;
  battleActionsTapDisposer?: () => void;
  // BACK button is owned by battleActionsHandle — see setBackVisible.
  menuMode: 'none' | 'main' | 'moves' | 'switch' | 'bag' | 'bagTarget' = 'none';
  /** G10: the item rows shown in 'bag' mode (registry order, ≤4) and
   *  the living targets shown in 'bagTarget' mode. Rebuilt on every
   *  menu entry; never read outside a live picker. */
  bagRows: BattleBagRow[] = [];
  bagTargets: BagTargetView[] = [];
  /** Item picked in 'bag' mode, consumed by confirmBagUse. */
  pendingBagItemId: string | null = null;
  /** Latest TurnRequest pulled from sim. Used to render the switch menu
   *  without a separate query, and to track which slot is active. */
  pendingRequest: TurnRequest | null = null;
  /** Resolves the choice loop's `await promptAction()` — the player picked
   *  a move (positive slot 1..4), tapped RUN (RUN_SENTINEL), or picked a
   *  switch target (SWITCH_OFFSET + slot). */
  pendingActionResolver?: (action: ActionResult) => void;
  /** Resolves a forceSwitch prompt — fired after a faint with reserves
   *  remaining; player can ONLY switch. */
  pendingForceSwitchResolver?: (slot: number) => void;

  /** Sim slot (1-indexed) of the currently active p1 battler. Parallel to
   *  activeId — kept as an informational view (no current readers depend on
   *  it; activeId is the canonical key for HP / permadeath). */
  activeSlot = 1;
  /** Id of the party member currently active in slot a (singles). Set at
   *  battle start to party[0].id; updated on every |switch|p1a via
   *  resolveActiveId. The load-bearing key for hpById writes + faintedIds
   *  membership — replaces the pre-v7 species heuristic that collided when
   *  two same-species Pokémon coexisted (the HP-by-species bug). */
  activeId: string | null = null;
  /** Bookkeeper for the outgoing `>p1 switch N` command. Set right before
   *  battle.chooseSwitch() fires; consumed by resolveActiveId when the
   *  matching incoming |switch|p1a arrives. null between switches and for
   *  sim-initiated paths (defensive path k handles those via the
   *  species + non-fainted fallback). */
  pendingSwitchToId: string | null = null;
  /** Party member ids that fainted this battle. Replaces faintedPartyIndices
   *  — id-keyed permadeath credit is race-free even with same-species
   *  duplicates (the index-based findIndex(species && !fainted) heuristic
   *  picked the wrong slot when the player force-switched into slot 2 first
   *  and fainted it before slot 1). Spliced from runState by predicate, not
   *  by index, so iteration order doesn't matter. */
  faintedIds = new Set<string>();
  /** Legacy boolean — true if any p1 fainted at all. Drives the
   *  appendLog faint message + game-over messaging. */
  playerFainted = false;

  /**
   * v7: per-INSTANCE current HP, keyed by PartyMember.id. Built from
   * runState at battle start, updated on every |damage|p1 event under the
   * active id, read on every |switch|p1 event for the bar, zeroed on
   * |faint|p1, flushed back to runState at battleEnd via setMemberHpById.
   *
   * Why we own this rather than reading from pendingRequest.team:
   *   1. The previous fix (commit 347cb35) trusted sim's request — but the
   *      condition string after a switch-IN didn't reflect preserved HP for
   *      reasons we never fully diagnosed (likely sim's request format
   *      quirk in gen3customgame). Owning HP in our layer is unambiguous.
   *   2. Cross-battle persistence requires us to have authoritative HP
   *      somewhere — we'd need this map even if sim's request worked.
   *   3. Same-species duplicates (two Pikachus) need per-INSTANCE HP. The
   *      pre-v7 species-keyed map collapsed them onto the same record;
   *      v7 keys by uuid so each slot's HP is independent.
   */
  hpById: Map<string, { current: number; max: number }> = new Map();

  /** Per-target latch for the next damage event's effectiveness. Showdown
   *  emits '-supereffective' / '-resisted' / '-immune' on the line before
   *  the matching '-damage', so the effectiveness handler stamps this and
   *  the damage handler reads + clears it. Anything else lands as 'normal'. */
  nextEffectiveness: Record<'p1' | 'p2', 'super' | 'resisted' | 'immune' | null> = {
    p1: null,
    p2: null,
  };

  /** Per-target latch for the next damage event's crit marker — same
   *  line-before-damage protocol contract as nextEffectiveness. Feeds
   *  hit-stop + the CRITICAL verdict pill (combat juice). */
  nextCrit: Record<'p1' | 'p2', boolean> = { p1: false, p2: false };

  /** Last |move| line's resolved TYPE per attacking side (lowercase,
   *  via the cached gen-3 dex; null when the lookup missed). The
   *  damage handler reads the ATTACKER's entry to pick the hit-FX
   *  skin — type picks the skin, effectiveness picks the magnitude. */
  lastMoveType: Record<'p1' | 'p2', string | null> = { p1: null, p2: null };

  /** Floating combat text overlay (damage numbers + verdict pills). */
  battleFloatText?: BattleFloatTextHandle;

  /**
   * EXP-attribution log. Each |faint|p2 event appends one entry recording
   * the opponent that fell + the active p1 ID at that moment (the "killer
   * of record"). At battleEnd we award EXP per entry to the killer's party
   * slot — multi-Pokémon parties get cartridge-correct per-kill attribution
   * rather than dumping all EXP on the last battler.
   *
   * `level` looked up from setup.p2.team[opponentFaints.length] BEFORE
   * push (sim's RandomPlayerAI sends opponents in team order, so the
   * Nth p2 faint corresponds to p2.team[N]). Wild battles always have
   * one entry; trainer battles have one per opponent.
   *
   * v7: killerId replaces killerSpecies. Same-species duplicates resolve
   * to the correct slot at battleEnd (the species heuristic would credit
   * the lower-slot one even when the higher-slot one made the KO).
   */
  opponentFaints: Array<{
    species: string;
    level: number;
    killerId: string;
  }> = [];

  /**
   * Choice gate: prevents rapid-clicking from queueing up multiple turns'
   * worth of moves while the previous turn's events are still animating.
   *
   * The simulator emits the next |request| (move-choice prompt) as soon as
   * it finishes processing the previous turn — far ahead of our event-
   * playback loop, which paces moves/damage/faint with delays. Without a
   * gate, the choice loop pulls the next prompt immediately and the player
   * can click before any animation has played.
   *
   * The gate releases on the next 'turn' event in playEvent (which is the
   * canonical "ready for next move" moment in pokeemerald's text/animation
   * order) and on 'battleEnd' (so any pending wait can cleanly exit when
   * the run concludes mid-turn).
   */
  choicesReady = false;
  choicesReadyResolver?: () => void;
  battleOver = false;

  constructor() {
    super('BattleScene');
  }

  init(data: BattleSceneData): void {
    this.params = data;
    this.opponentHpPercent = 100;
    // v7: build hpById from runState. The party order in runState matches
    // setup.p1.team order (teamFromParty preserves it). Keys by id so two
    // same-species Pokémon get independent HP records.
    this.hpById = new Map();
    for (const m of runState.getParty()) {
      this.hpById.set(m.id, { current: m.hp.current, max: m.hp.max });
    }
    // Initial player active is party[0] — its HP percent seeds the bar
    // and its id seeds activeId (sim emits the first |switch|p1a at battle
    // start; the resolver's fallback path lands on this same id, but we
    // set it explicitly so any reads before that event are correct).
    const first = runState.getParty()[0];
    this.activeId = first?.id ?? null;
    this.playerHpPercent =
      first && first.hp.max > 0
        ? Math.round((first.hp.current / first.hp.max) * 100)
        : 100;
    this.battleLogHandle?.clear();
    this.playerFainted = false;
    // Phaser doesn't clear class-instance properties on scene shutdown — only
    // the display list. The GameObject refs below are stale after a previous
    // run; the next create() rebuilds them fresh, but the array of move
    // buttons would otherwise grow each time we re-enter the scene.
    this.menuMode = 'none';
    this.pendingRequest = null;
    this.pendingActionResolver = undefined;
    this.pendingForceSwitchResolver = undefined;
    this.bagRows = [];
    this.bagTargets = [];
    this.pendingBagItemId = null;
    this.activeSlot = 1;
    this.pendingSwitchToId = null;
    this.faintedIds = new Set<string>();
    this.opponentFaints = [];
    this.choicesReady = false;
    this.choicesReadyResolver = undefined;
    this.battleOver = false;
    this.nextEffectiveness = { p1: null, p2: null };
    this.nextCrit = { p1: false, p2: false };
    this.lastMoveType = { p1: null, p2: null };
  }

  waitForChoicesReady(): Promise<void> {
    return waitForChoicesReadyImpl.call(this);
  }

  signalChoicesReady(): void {
    return signalChoicesReadyImpl.call(this);
  }

  preload(): void {
    // Combat-juice hit FX: 17 cartridge battle-anim sheets, ~8.5KB
    // total. Loaded every battle mount; Phaser's texture cache makes
    // re-entry a no-op.
    preloadBattleFx(this.load);
    this.load.image(
      `pkmn-front:${this.params.opponentDexId}`,
      `assets/pokemon/front/${this.params.opponentDexId}.png`,
    );
    // Preload front sprites for the WHOLE p2 team, not just the lead —
    // Brock's Onix switched in as Phaser's __MISSING black/green box
    // because only opponentDexId was ever loaded (playtest round 2).
    // Fronts are ~0.5-1KB each, ≤6 per trainer; dupe keys are cache
    // no-ops.
    const seenFront = new Set<number>([this.params.opponentDexId]);
    for (const member of this.params.setup.p2.team) {
      const dexId = SPECIES_TO_DEX[member.species];
      if (dexId === undefined || seenFront.has(dexId)) continue;
      seenFront.add(dexId);
      this.load.image(`pkmn-front:${dexId}`, `assets/pokemon/front/${dexId}.png`);
    }
    // Preload back sprites for ALL party members so the player-sprite swap
    // on switch doesn't pop a missing-texture rect mid-battle. v0.1.x has
    // small parties (≤6) so the bytes are negligible.
    const seen = new Set<number>();
    seen.add(this.params.playerDexId);
    this.load.image(
      `pkmn-back:${this.params.playerDexId}`,
      `assets/pokemon/back/${this.params.playerDexId}.png`,
    );
    for (const member of this.params.setup.p1.team) {
      const dexId = SPECIES_TO_DEX[member.species];
      if (dexId === undefined || seen.has(dexId)) continue;
      seen.add(dexId);
      this.load.image(`pkmn-back:${dexId}`, `assets/pokemon/back/${dexId}.png`);
    }
    // m5x7 is loaded by WorldScene; cache is global so we can use it here.
  }

  create(): void {
    // Glass-Over-World PR 2: claim the WorldScene-parented snapshot as
    // our FIRST act so the DOM-glass chrome blurs the frozen world
    // underneath instead of opaque `#0E1014`. Eng-locked #4. If the
    // capture timed out / errored, fall back to the prior dark fill.
    if (this.params.snapshotHandle) {
      this.params.snapshotHandle.transferOwnership(this);
    } else {
      this.cameras.main.setBackgroundColor('#0E1014');
    }
    // Liquid Glass battle stage — bakes a blur of the snapshot, then
    // stacks the scrim wash + glass panel behind where createObjects()
    // will insert the battlers. Must run between transferOwnership
    // (above) and createObjects (below) so display-list insertion
    // order layers it correctly.
    this.battleStage = buildBattleStage(this, this.params.snapshotHandle ?? null);
    // Phase 15a: switch to battle music. Trainer battles consult the
    // per-trainer override table (Brock = gym leader theme); wild battles
    // use the generic wild theme. Crossfade duration kept short — the
    // encounter cue SFX bridges the silence between map music + battle.
    const ref = this.params.trainerScriptRef;
    const battleKey = ref ? (TRAINER_BATTLE_MUSIC[ref] ?? 'trainer_battle') : 'wild_battle';
    void audio.playMusic(this, battleKey, { fadeMs: 400 });
    // PR 2.5: warm the type-effectiveness matrix so the move-menu's
    // matchup badges resolve synchronously when showMoveMenu fires.
    // Fire-and-forget — the badge code defensively returns neutral on
    // cache miss, so a slow preload just means the first turn's badge
    // patches in a moment late.
    void preloadTypeChart();
    this.createObjects();
    this.layout();
    this.createActionButtons();
    // The Scale plugin is a game-level singleton; listeners survive scene
    // shutdown unless explicitly removed. Without this, every fresh battle
    // would leak one more resize listener from the previous run.
    const onResize = (): void => this.layout();
    this.scale.on('resize', onResize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off('resize', onResize);
    });
    void this.run();
  }

  /**
   * Build the static GameObjects (sprites, HP bars, labels, log box). Called
   * once from create() per scene init. Always creates fresh objects — DON'T
   * reuse stale refs from a previous run, because Phaser cleared the display
   * list on shutdown but kept the JS properties pointing at destroyed
   * objects. (Re-using them was a bug that hung the scene in CREATING state
   * the second time the player entered a battle.)
   */
  createObjects(): void {
    this.opponentSprite = this.add
      .image(0, 0, `pkmn-front:${this.params.opponentDexId}`)
      .setOrigin(1, 0)
      .setScale(SPRITE_SCALE);
    this.opponentStatusPanel = mountBattleStatusPanel(this, { side: 'opponent' });

    this.playerSprite = this.add
      .image(0, 0, `pkmn-back:${this.params.playerDexId}`)
      .setOrigin(0, 1)
      .setScale(SPRITE_SCALE);
    this.playerStatusPanel = mountBattleStatusPanel(this, { side: 'player' });

    this.battleLogHandle = mountBattleLog(this);
    this.dialogueOverlay = mountDialogueOverlay(this);
    this.battleFloatText = mountBattleFloatText(this);
    // Tear DOM-shell handles down on SHUTDOWN. exitToWorld /
    // exitToEndRun call scene.stop(), which fires SHUTDOWN (not SLEEP)
    // — without this, the DOM panels these handles own (status panels,
    // log, dialogue) stay mounted in #game over the overworld. Mirrors
    // the actions cleanup in createActionButtons. Status panels destroy
    // their inner HP bars transitively.
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.opponentStatusPanel?.destroy();
      this.opponentStatusPanel = undefined;
      this.playerStatusPanel?.destroy();
      this.playerStatusPanel = undefined;
      this.battleLogHandle?.destroy();
      this.battleLogHandle = undefined;
      this.dialogueOverlay?.destroy();
      this.dialogueOverlay = undefined;
      this.battleFloatText?.destroy();
      this.battleFloatText = undefined;
      // Defensive: clear cinematic body classes so they don't leak
      // into a future scene if the player exits mid-cinematic.
      const gameEl = document.getElementById('game');
      gameEl?.classList.remove('is-battle-fainting');
      gameEl?.classList.remove('is-last-alive-critical');
      gameEl?.classList.remove('is-super-effective');
      gameEl?.classList.remove('is-battle-defeat');
    });

    // Initial state sync (one-shot — switch events handle changes).
    const opp0 = this.params.setup.p2.team[0];
    if (opp0) {
      this.opponentStatusPanel.setName(opp0.name ?? capitalize(opp0.species));
      this.opponentStatusPanel.setLevel(opp0.level ?? 1);
      this.applyHpBarType('p2', opp0.species);
    }
    const p0 = this.params.setup.p1.team[0];
    if (p0) {
      this.playerStatusPanel.setName(p0.name ?? capitalize(p0.species));
      this.playerStatusPanel.setLevel(p0.level ?? 1);
      const hp = this.activeId ? this.hpById.get(this.activeId) : null;
      if (hp) {
        this.playerStatusPanel.setHp(hp.max > 0 ? hp.current / hp.max : 0, true);
        this.playerStatusPanel.setHpNumeric(hp.current, hp.max);
      }
      const member = runState.getParty()[0];
      if (member) this.playerStatusPanel.setExp(member.exp, member.level);
      this.applyHpBarType('p1', p0.species);
      // PR 3 EXP 5e: apply the lead's persistent status tint at battle
      // start so a paralyzed reserve carrying status from the previous
      // battle reads visually immediately on screen, not after the
      // first |status| event.
      if (member) applyStatusTint(this, this.playerSprite, member.status);
    }
    // Initial last-alive cinematic check — covers the case where the
    // player starts the battle with a single Pokémon already below 10 %
    // HP (e.g., walking into Brock after a Route 1 grind that left the
    // only survivor at 3 HP).
    this.updateLastAliveCriticalState();
  }

  /**
   * Build 4 action buttons in a 2x2 grid (bottom-right). The same buttons
   * cycle between the main menu (FIGHT / POKEMON / BAG / RUN) and the move
   * submenu (Pound / Leer / etc.) — the click handler dispatches off
   * `this.menuMode` so we don't have two parallel sets of GameObjects to
   * keep aligned.
   *
   * Hidden by default; promptAction → showMainMenu reveals them at the
   * start of each turn, after the gate releases.
   */
  createActionButtons(): void {
    this.battleActionsHandle = mountBattleActions(this);
    this.battleActionsTapDisposer = this.battleActionsHandle.onTap((target) => {
      if (target === 'back') this.onBackClick();
      else this.onActionButtonClick(target);
    });
    // B and Escape keys trigger BACK from moves/switch (skipped during
    // force-switch — there's nothing to back out to). Same SHUTDOWN
    // teardown as the resize listener above; Phaser keyboard plugin
    // survives scene shutdown unless detached.
    const bKey = this.input.keyboard?.on('keydown-B', () => this.onBackClick());
    const escKey = this.input.keyboard?.on('keydown-ESC', () => this.onBackClick());
    // PR 2.5: 1-4 number keys submit move slots when the move menu is up
    // (DESIGN.md accessibility baseline — desktop keyboard nav). Same
    // semantics as a tap on the corresponding slot button.
    const numKeys: Array<Phaser.Input.Keyboard.Key | undefined> = [];
    for (let i = 1; i <= 4; i++) {
      const k = this.input.keyboard?.on(`keydown-${i}` as 'keydown-1', () => {
        if (this.menuMode === 'moves') this.onActionButtonClick(i);
      });
      numKeys.push(k as Phaser.Input.Keyboard.Key | undefined);
    }
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      bKey?.off('keydown-B');
      escKey?.off('keydown-ESC');
      for (let i = 0; i < numKeys.length; i++) {
        numKeys[i]?.off(`keydown-${i + 1}`);
      }
      this.battleActionsTapDisposer?.();
      this.battleActionsHandle?.destroy();
      this.battleActionsHandle = undefined;
      this.battleActionsTapDisposer = undefined;
    });
  }

  onBackClick(): void {
    return onBackClickImpl.call(this);
  }

  hideActionButtons(): void {
    return hideActionButtonsImpl.call(this);
  }

  onActionButtonClick(slot: number): void {
    return onActionButtonClickImpl.call(this, slot);
  }

  showMainMenu(): void {
    return showMainMenuImpl.call(this);
  }

  showMoveMenu(): void {
    return showMoveMenuImpl.call(this);
  }

  showBagMenu(): void {
    return showBagMenuImpl.call(this);
  }

  showBagTargetMenu(): void {
    return showBagTargetMenuImpl.call(this);
  }

  confirmBagUse(slot: number): void {
    return confirmBagUseImpl.call(this, slot);
  }

  showSwitchMenu(): void {
    return showSwitchMenuImpl.call(this);
  }

  promptAction(): Promise<ActionResult> {
    return promptActionImpl.call(this);
  }

  promptForceSwitch(): Promise<number> {
    return promptForceSwitchImpl.call(this);
  }

  async runFromBattle(): Promise<void> {
    return runFromBattleImpl.call(this);
  }

  flushSurvivorHpToRunState(): void {
    return flushSurvivorHpToRunStateImpl.call(this);
  }

  flushBattleStatusToRunState(battle: InteractiveBattle): void {
    return flushBattleStatusToRunStateImpl.call(this, battle);
  }

  spliceFaintedFromRunState(): void {
    return spliceFaintedFromRunStateImpl.call(this);
  }

  /** Position the static GameObjects per the current viewport. Called once
   *  from create() (after createObjects) + on every resize. Does no
   *  construction — that's createObjects()'s job, kept separate so re-
   *  entering the scene a second time doesn't try to reuse stale refs. */
  /** Host seam (M2): swap the visible battler art on a |switch| event.
   *  BattleScene's staging uses the 4x battle stills; the in-world
   *  duel host overrides to re-skin its OW walker sprite instead. The
   *  guard (dex known + sprite present) lives at the call site so the
   *  downstream panel bookkeeping is shared verbatim. */
  applySwitchVisual(side: 'p1' | 'p2', species: string): void {
    const dexId = SPECIES_TO_DEX[species];
    if (dexId === undefined) return;
    if (side === 'p1' && this.playerSprite) {
      this.playerSprite.setTexture(`pkmn-back:${dexId}`);
      this.playerSprite.setAlpha(1);
    } else if (side === 'p2' && this.opponentSprite) {
      this.opponentSprite.setTexture(`pkmn-front:${dexId}`);
      this.opponentSprite.setAlpha(1);
    }
  }

  /** Host seam (M2): scene→screen projection for DOM float anchors.
   *  BattleScene's camera never zooms or scrolls, so identity here;
   *  the duel host projects through the punched-in world camera. */
  toScreen(x: number, y: number): { x: number; y: number } {
    return { x, y };
  }

  /** Host seam (M2): scene-px → screen-px scale factor for sprite
   *  extents used in DOM anchor math. 1 in BattleScene; the duel
   *  host returns the world camera's zoom. */
  screenScale(): number {
    return 1;
  }

  /** Host seam (M2): spatial multiplier for impact-FX bursts. 1 here
   *  (the 4x-still staging the FX were tuned against); the duel host
   *  passes its battler screen-size ratio so bursts stay proportional
   *  to 32px walkers under the punched camera. */
  hitFxScale(): number {
    return 1;
  }

  /** Host seam (M2): the resting scale of the active p1 battler — the
   *  level-up pulse tweens from/to this base. 4x battle stills here;
   *  the duel host's OW walker rests at 1 (tweening from a hardcoded
   *  SPRITE_SCALE would balloon the walker to 4x and leave it there —
   *  the yoyo returns to `from`). */
  battlerBaseScale(): number {
    return SPRITE_SCALE;
  }

  layout(): void {
    const w = this.scale.width;
    const h = this.scale.height;
    const margin = 24;

    // Opponent (top-right area): sprite — name + HP bar live in
    // opponentStatusPanel which self-anchors via CSS.
    this.opponentSprite.setPosition(w - margin, margin);

    // Player (bottom-left area). playerSpriteY (= sprite bottom edge,
    // since setOrigin(0, 1)) is the smaller of two caps:
    //
    // - opponentGapCap: margin (24) + spritePx (256) + 32 px gap +
    //   spritePx (256) = 568. On tall canvases, anchor the player a
    //   fixed 32 px below opponent so dead space doesn't open up
    //   between sprites.
    // - logClearanceCap: 8 px above the battle log's top edge —
    //   BATTLE_LOG_TOP_ESTIMATE_PX (derived through battleLog ←
    //   battleActions; the round-3 scale-up replaced the hand-synced
    //   292/304/391/403 chain with exported constants).
    //
    // On tall canvases the opponent cap wins; on shorter ones the log
    // cap wins, accepting some vertical overlap with the opponent
    // (horizontally offset, so visually clean — cartridge convention).
    const spritePx = 64 * SPRITE_SCALE;
    const opponentGapCap = margin + spritePx + 32 + spritePx;
    const logClearanceCap = h - BATTLE_LOG_TOP_ESTIMATE_PX - 8;
    const playerSpriteY = Math.min(opponentGapCap, logClearanceCap);
    this.playerSprite.setPosition(margin, playerSpriteY);

    // Glass stage tracks the same geometry — panel bottom hugs the
    // player sprite's feet.
    this.battleStage?.layout(w, h, playerSpriteY);

    // Player status panel (name + level + HP bar + HP numeric + EXP)
    // self-anchors above the log via CSS; no Phaser positioning here.
    // Battle log + action buttons also self-anchor via their shells.
  }

  /** Live InteractiveBattle handle for the current battle. Set in run();
   *  read by the eventHandlers' battleEnd flush + runFromBattle's flush
   *  so status can round-trip through sim's internal pokemon[i].status.
   *  null between battles. v10. */
  battle: InteractiveBattle | null = null;

  async run(): Promise<void> {
    return runImpl.call(this);
  }

  async playEvent(event: BattleEvent): Promise<void> {
    return playEventImpl.call(this, event);
  }

  appendLog(text: string): void {
    return appendLogImpl.call(this, text);
  }

  applyHpBarType(side: 'p1' | 'p2', species: string): void {
    return applyHpBarTypeImpl.call(this, side, species);
  }

  tweenAttacker(side: 'p1' | 'p2'): Promise<void> {
    return tweenAttackerImpl.call(this, side);
  }

  tweenFaint(sprite: Phaser.GameObjects.Image): Promise<void> {
    return tweenFaintImpl.call(this, sprite);
  }

  updateLastAliveCriticalState(): void {
    return updateLastAliveCriticalStateImpl.call(this);
  }

  /**
   * Player-faint cinematic per design doc rev 6 lines 420-428.
   *
   *   t=0       triggerFaint() + body class added; sprite drop tween
   *             runs concurrently (caller awaits both).
   *   t=0-300   HP bar ::after red overlay pulses 3× (CSS animation).
   *   t=300-900 HP bar ::before type-tint drains 1 → 0 (CSS).
   *   t=600-1200 canvas saturate drops 1.0 → 0.6 (CSS on #game).
   *   t=900     appendLog "{Name} has been lost."
   *   t=2500    remove body class; saturate returns to 1.0 (CSS, 400 ms).
   *
   * Returns when the 2500 ms timed window completes. No user input
   * required — the original dialogueOverlay-based variant hung the
   * sim's event loop when the dialogue's tap-to-dismiss path stalled
   * during a force-switch (user report 2026-05-11: "after my pidgey
   * died ... I hit charmander but nothing happened"). The cinematic's
   * emotional weight comes from the visual beats; the "has been lost"
   * line still lands in the log. Auto-advance also matches what the
   * cartridge does (faint resolves itself; player picks next mon).
   */
  async runPlayerFaintCinematic(lostName: string): Promise<void> {
    return runPlayerFaintCinematicImpl.call(this, lostName);
  }

  async runOpponentDeathFade(sprite: Phaser.GameObjects.Image): Promise<void> {
    return runOpponentDeathFadeImpl.call(this, sprite);
  }

  async runVictoryHero(): Promise<void> {
    return runVictoryHeroImpl.call(this);
  }

  async runDefeatCinematic(): Promise<void> {
    return runDefeatCinematicImpl.call(this);
  }

  wait(ms: number): Promise<void> {
    return waitImpl.call(this, ms);
  }

  exitToWorld(): void {
    return exitToWorldImpl.call(this);
  }

  /**
   * Phase 12 M5: route to EndRunScene instead of waking the WorldScene.
   * The WorldScene stays asleep — the run is over and the EndRunScene's
   * Reset Run reloads the page, tearing both scenes down naturally. We
   * `scene.stop()` ourselves so when the page reloads, BattleScene
   * isn't left in a half-state. Wrapped in try/catch per the failure-
   * mode plan (M5 critical gap): if EndRunScene launch fails for any
   * reason, fall back to alert + reload so the player can't softlock.
   */
  exitToEndRun(
    mode: 'win' | 'defeat',
    casualties: Array<{ species: string; level: number }>,
  ): void {
    return exitToEndRunImpl.call(this, mode, casualties);
  }

  /** Used by Phaser's BattleScene boot helper to rebuild the log on init. */
  public clearLog(): void {
    this.battleLogHandle?.clear();
  }

  refreshPlayerExp(species: string): void {
    return refreshPlayerExpImpl.call(this, species);
  }
}

export function capitalize(s: string): string {
  if (!s) return s;
  return s[0]!.toUpperCase() + s.slice(1);
}


